import React, { createContext, useContext, useState, useEffect } from 'react';
import { useFavicon } from '../hooks/useFavicon';

const SiteSettingsContext = createContext();

export const useSiteSettings = () => {
  const context = useContext(SiteSettingsContext);
  if (!context) {
    throw new Error('useSiteSettings must be used within SiteSettingsProvider');
  }
  return context;
};

export const SiteSettingsProvider = ({ children }) => {
  const [siteSettings, setSiteSettings] = useState({
    site_title: null,
    logo_base64: null,
    favicon_base64: null,
    contact_email: 'info@keyzuvi.com',
    contact_phone: '+90 850 840 6231',
    social_facebook: 'https://facebook.com/keyzuvi',
    social_twitter: 'https://twitter.com/keyzuvi',
    social_instagram: 'https://instagram.com/keyzuvi',
    social_youtube: 'https://youtube.com/keyzuvi',
    social_linkedin: 'https://linkedin.com/company/keyzuvi',
    social_discord: 'https://discord.gg/keyzuvi'
  });
  const [loading, setLoading] = useState(true);

  // Site ayarlarını yükle
  useEffect(() => {
    const loadSiteSettings = async () => {
      try {
        // Önce localStorage'dan hızlı yükle (sadece geçerli veri varsa)
        const cachedSettings = localStorage.getItem('siteSettings');
        if (cachedSettings) {
          try {
            const parsedSettings = JSON.parse(cachedSettings);
            // Cache'deki verinin geçerliliğini kontrol et
            if (parsedSettings.site_title && parsedSettings.site_title !== null) {
              setSiteSettings(parsedSettings);
            }
          } catch (parseError) {
            localStorage.removeItem('siteSettings');
          }
        }

        // API'den güncel veriyi al
        const response = await fetch(`http://localhost:8001/api/site-settings`);
        if (response.ok) {
          const data = await response.json();
          // Sadece geçerli veri varsa güncelle
          if (data.site_title && data.site_title !== null) {
            setSiteSettings(data);
            // Cache'e kaydet
            localStorage.setItem('siteSettings', JSON.stringify(data));
          }
        }
      } catch (error) {
        // Hata durumunda varsayılan değerleri kullan
        setSiteSettings(prev => ({
          ...prev,
          site_title: 'Keyzuvi Hosting',
          logo_base64: null,
          favicon_base64: null
        }));
      } finally {
        setLoading(false);
      }
    };

    loadSiteSettings();
  }, []);

  // Favicon'u dinamik olarak yükle
  useFavicon(siteSettings.favicon_base64);

  // Site başlığını dinamik olarak güncelle
  useEffect(() => {
    const title = siteSettings.site_title || 'Keyzuvi Hosting';
    if (title && title !== null) {
      const currentPath = window.location.pathname;
      // Admin sayfasında (/kedihost) farklı başlık kullan
      if (currentPath.startsWith('/kedihost') || currentPath.startsWith('/admin')) {
        document.title = `${title} - Admin Panel`;
      } else if (currentPath !== '/dashboard') {
        document.title = title;
      }
    }
  }, [siteSettings.site_title]);

  // Cache'i temizle ve yeniden yükle
  const refreshSiteSettings = async () => {
    localStorage.removeItem('siteSettings');
    const response = await fetch(`http://localhost:8001/api/site-settings?t=${Date.now()}`);
    if (response.ok) {
      const data = await response.json();
      setSiteSettings(data);
      localStorage.setItem('siteSettings', JSON.stringify(data));
    }
  };

  const value = {
    siteSettings,
    setSiteSettings,
    loading,
    refreshSiteSettings
  };

  return (
    <SiteSettingsContext.Provider value={value}>
      {children}
    </SiteSettingsContext.Provider>
  );
};

